/**
 * "+ Direct URL" offer dialog — prompts for a URL and attaches a special
 * "directUrl:<url>" offer entry. The merger maps these to Binom's
 * directUrl-as-offer convention server-side.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { Button } from '@/components/ui/layout/Button';
import { Input } from '@/components/ui/form/input/Input';

import type { BinomDraftItem } from '../../../binom-flow-draft.types';

export interface BinomDirectUrlOfferDialogProps {
  open: boolean;
  onClose: () => void;
  onAttach: (item: BinomDraftItem) => void;
}

export function makeDirectUrlOfferItem(url: string): BinomDraftItem {
  const trimmed = url.trim();
  return {
    id: `directUrl:${trimmed}`,
    name: trimmed,
    weight: 100,
    originalWeight: 100,
    active: true,
    originalActive: true,
  };
}

const BinomDirectUrlOfferDialog: FC<BinomDirectUrlOfferDialogProps> = ({
  open,
  onClose,
  onAttach,
}) => {
  const [url, setUrl] = useState('');
  const submit = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    onAttach(makeDirectUrlOfferItem(trimmed));
    setUrl('');
    onClose();
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent variant="center" width="w-[32rem]">
        <DrawerHeader>
          <DrawerTitle>Attach direct-URL offer</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/lp/?click_id={click_id}"
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            autoFocus
            className="h-8 text-xs"
          />
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={submit} disabled={!url.trim()}>
            Attach
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BinomDirectUrlOfferDialog;
